import { useState } from 'react';
import { act } from '../../lib/actions';
import type { FileView, WaTemplate } from '../../lib/types';
import { blanksOf, HeaderFilePicker, isMedia, MEDIA_HEADERS, preview } from '../WaTemplates';
import { api } from '../../lib/api';
import { Button } from '../ui';
import { AddButton, Cell, ConfirmButton, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, Toggle, useLoad } from './kit';

interface Account {
  id: string;
  name: string;
  phone_number_id: string;
  waba_id: string;
  display_phone: string;
  verified_name: string;
  verify_token: string;
  webhook_url: string;
  queue_id: string | null;
  queue_name: string | null;
  business_hours_id: string | null;
  greeting: string;
  offline_message: string;
  active: boolean;
  last_error: string | null;
}
interface Named {
  id: string;
  name: string;
  channel?: string;
}

/**
 * WhatsApp numbers on Meta's WhatsApp Business Platform: customers message
 * the company's WhatsApp number, and the chats come to agents like livechat.
 */
export function WhatsApp() {
  const [accounts, reload] = useLoad<Account[]>('/whatsapp-accounts');
  const [editing, setEditing] = useState<Account | 'new' | null>(null);
  return (
    <>
      <SectionHeader
        title="WhatsApp"
        lead="Customers message your WhatsApp Business number, and the chats come to agents like livechat, under their chat limit. You need a number on Meta's WhatsApp Business Platform; Nova shows what to paste back into Meta."
        action={<AddButton onClick={() => setEditing('new')}>Connect a number</AddButton>}
      />
      <Table head={['Number', 'Chats go to', 'Status']} empty={accounts?.length === 0 && 'No numbers yet. Connect one to take WhatsApp chats.'}>
        {(accounts ?? []).map((a) => (
          <Row key={a.id} onClick={() => setEditing(a)}>
            <Cell>
              <span className="block font-medium">{a.name}</span>
              <span className="tabular text-[12px] text-ink-3">{a.display_phone}</span>
            </Cell>
            <Cell>{a.queue_name ?? <span className="text-break">No queue: chats cannot start</span>}</Cell>
            <Cell>
              {!a.active ? <Pill>Off</Pill> : a.last_error ? <Pill tone="bad">Error</Pill> : <Pill tone="good">On</Pill>}
              {a.active && a.last_error ? <span className="mt-1 block max-w-[260px] text-[12px] text-break">{a.last_error}</span> : null}
            </Cell>
          </Row>
        ))}
      </Table>
      {editing ? (
        <AccountDrawer
          account={editing === 'new' ? null : editing}
          onDone={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function Copy({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mb-3">
      <p className="mb-1 text-[12px] font-medium">{label}</p>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 break-all rounded bg-abyss px-2 py-1.5 text-[11px]" aria-label={label}>
          {value}
        </code>
        <Button tone="quiet" className="px-2.5 py-1 text-xs" onClick={() => void navigator.clipboard?.writeText(value).then(() => setCopied(true))}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

function AccountDrawer({ account, onDone }: { account: Account | null; onDone: () => void }) {
  const [queues] = useLoad<Named[]>('/queues');
  const [hours] = useLoad<Named[]>('/business-hours');
  const waQueues = (queues ?? []).filter((q) => q.channel === 'whatsapp');
  const [saved, setSaved] = useState<Account | null>(account);
  const [f, setF] = useState({
    phoneNumberId: account?.phone_number_id ?? '',
    wabaId: account?.waba_id ?? '',
    token: '',
    appSecret: '',
    queueId: account?.queue_id ?? '',
    businessHoursId: account?.business_hours_id ?? '',
    greeting: account?.greeting ?? 'Thanks for your message! An agent will reply here shortly.',
    offlineMessage: account?.offline_message ?? 'We are closed right now. We will reply when we open.',
    active: account?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));

  async function save() {
    setBusy(true);
    const body = { ...f, token: f.token.trim() || undefined, appSecret: f.appSecret.trim() || undefined, businessHoursId: f.businessHoursId || null };
    const r = await act(() => (saved ? api<Account>('PATCH', `/whatsapp-accounts/${saved.id}`, body) : api<Account>('POST', '/whatsapp-accounts', body)), saved ? 'Saved' : 'Number connected');
    setBusy(false);
    // A new number stays open: its webhook details go into Meta next
    if (r && !saved) {
      setSaved(r);
      set('token', '');
      set('appSecret', '');
    } else if (r) onDone();
  }
  async function remove() {
    if (await act(() => api('DELETE', `/whatsapp-accounts/${saved!.id}`).then(() => true), 'Number disconnected')) onDone();
  }

  return (
    <Drawer
      title={saved ? `${saved.name} (${saved.display_phone})` : 'Connect a WhatsApp number'}
      open
      onClose={onDone}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || !f.queueId || !f.phoneNumberId || !f.wabaId || (!saved && (!f.token.trim() || !f.appSecret.trim()))} onClick={() => void save()}>
            {busy ? 'Checking with Meta…' : saved ? 'Save' : 'Connect'}
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onDone}>
            {saved && !account ? 'Done' : 'Cancel'}
          </Button>
          {saved ? (
            <span className="ml-auto">
              <ConfirmButton confirmText="Disconnect it" onConfirm={() => void remove()}>
                Disconnect
              </ConfirmButton>
            </span>
          ) : null}
        </>
      }
    >
      {saved ? (
        <div className="mb-5 rounded-lg border border-line p-3" aria-label="Meta webhook settings">
          <p className="mb-1 text-sm font-medium">In Meta: send this number's messages to Nova</p>
          <p className="mb-3 text-[12px] text-ink-3">
            developers.facebook.com &gt; your app &gt; WhatsApp &gt; Configuration &gt; Webhook &gt; Edit: paste these two, Verify and save. Then under Webhook fields, subscribe to <b>messages</b>.
          </p>
          <Copy label="Callback URL" value={saved.webhook_url} />
          <Copy label="Verify token" value={saved.verify_token} />
        </div>
      ) : (
        <ol className="mb-4 list-decimal space-y-1 pl-5 text-[13px] text-ink-2">
          <li>At developers.facebook.com, open (or create) a Business app with the WhatsApp product, and add your number.</li>
          <li>WhatsApp &gt; API Setup shows the <b>Phone number ID</b> and <b>WhatsApp Business Account ID</b>.</li>
          <li>Business settings &gt; System users: make a permanent <b>access token</b> with whatsapp_business_messaging and whatsapp_business_management.</li>
          <li>App settings &gt; Basic shows the <b>App secret</b>.</li>
        </ol>
      )}
      <Field label="Phone number ID" hint={saved ? 'Fixed: a different number is a new connection.' : 'From WhatsApp > API Setup'}>
        {(id) => <input id={id} inputMode="numeric" className={`${inputClass} tabular`} value={f.phoneNumberId} disabled={Boolean(saved)} onChange={(e) => set('phoneNumberId', e.target.value.trim())} />}
      </Field>
      <Field label="WhatsApp Business Account ID">
        {(id) => <input id={id} inputMode="numeric" className={`${inputClass} tabular`} value={f.wabaId} onChange={(e) => set('wabaId', e.target.value.trim())} />}
      </Field>
      <Field label="Access token" hint={saved ? 'Leave empty to keep the saved one.' : 'A system user\'s permanent token; Nova keeps it encrypted.'}>
        {(id) => <input id={id} type="password" autoComplete="off" className={inputClass} value={f.token} onChange={(e) => set('token', e.target.value)} />}
      </Field>
      <Field label="App secret" hint={saved ? 'Leave empty to keep the saved one.' : 'Nova uses it to check that messages really come from Meta.'}>
        {(id) => <input id={id} type="password" autoComplete="off" className={`${inputClass} tabular`} value={f.appSecret} onChange={(e) => set('appSecret', e.target.value)} />}
      </Field>
      <Field label="Chats go to" hint={waQueues.length ? 'A WhatsApp queue. Its skills decide who gets the chats.' : 'Add a queue with channel WhatsApp under Queues first.'}>
        {(id) => (
          <select id={id} className={inputClass} value={f.queueId} onChange={(e) => set('queueId', e.target.value)}>
            <option value="">Choose a WhatsApp queue</option>
            {waQueues.map((q) => (
              <option key={q.id} value={q.id}>
                {q.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Open hours" hint="Outside them, a new chat gets the closed message and waits for the team. Empty: the queue's hours.">
        {(id) => (
          <select id={id} className={inputClass} value={f.businessHoursId} onChange={(e) => set('businessHoursId', e.target.value)}>
            <option value="">The queue's hours</option>
            {(hours ?? []).map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Greeting" hint="Sent when a customer starts a new chat. Empty: none.">
        {(id) => <textarea id={id} rows={2} className={inputClass} value={f.greeting} onChange={(e) => set('greeting', e.target.value)} />}
      </Field>
      <Field label="When closed" hint="Sent instead of the greeting outside open hours. Empty: none.">
        {(id) => <textarea id={id} rows={2} className={inputClass} value={f.offlineMessage} onChange={(e) => set('offlineMessage', e.target.value)} />}
      </Field>
      <Toggle label="Number on" checked={f.active} onChange={(v) => set('active', v)} />
      {saved ? <Templates accountId={saved.id} /> : null}
    </Drawer>
  );
}

const STATUS_TONE: Record<string, 'good' | 'bad' | 'warn' | 'line'> = { APPROVED: 'good', REJECTED: 'bad', PAUSED: 'warn', DISABLED: 'bad', PENDING: 'warn' };

/**
 * The number's message templates, as Meta has them: synced from Meta, and
 * new ones written here and sent to Meta for approval.
 */
function Templates({ accountId }: { accountId: string }) {
  const [list, reload] = useLoad<WaTemplate[]>(`/whatsapp/templates?accountId=${accountId}`);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  async function sync() {
    setBusy(true);
    const r = await act(() => api<{ templates: number }>('POST', '/whatsapp/templates/sync', { accountId }));
    setBusy(false);
    if (r) reload();
  }
  return (
    <section className="mt-6 border-t border-line pt-5" aria-label="Message templates">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="font-medium">Message templates</h3>
        <Button tone="quiet" className="ml-auto px-2.5 py-1 text-xs" disabled={busy} onClick={() => void sync()}>
          {busy ? 'Syncing…' : 'Sync from Meta'}
        </Button>
        <Button tone="quiet" className="px-2.5 py-1 text-xs" onClick={() => setAdding((v) => !v)}>
          New template
        </Button>
      </div>
      <p className="mb-3 text-[12px] text-ink-3">
        The only messages WhatsApp allows 24 hours after a customer last wrote, or to start a conversation. Meta approves each one; Nova checks for changes every hour.
      </p>
      {adding ? (
        <NewTemplate
          accountId={accountId}
          onDone={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
      {list?.length === 0 ? <p className="text-sm text-ink-2">None yet. Sync from Meta, or write one.</p> : null}
      <ul className="flex flex-col gap-2">
        {(list ?? []).map((t) => (
          <li key={t.id} className="rounded-lg border border-line p-2.5">
            <div className="flex items-center gap-2">
              <span className="tabular font-medium">{t.name}</span>
              <span className="text-[12px] text-ink-3">
                {t.language} · {t.category.toLowerCase()}
              </span>
              <span className="ml-auto">
                <Pill tone={STATUS_TONE[t.status] ?? 'line'}>{t.status.toLowerCase()}</Pill>
              </span>
            </div>
            <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-[12px] text-ink-2">{preview(t, {})}</p>
            {t.rejectedReason ? <p className="mt-1 text-[12px] text-break">Meta: {t.rejectedReason.toLowerCase().replace(/_/g, ' ')}</p> : null}
            {t.unsupported ? <p className="mt-1 text-[12px] text-ink-3">Nova cannot send this one yet: {t.unsupported.toLowerCase()}.</p> : null}
            {isMedia(t) ? <TemplateFile t={t} onDone={reload} /> : null}
            <div className="mt-1 text-right">
              <ConfirmButton confirmText="Delete at Meta too" onConfirm={() => void act(() => api('DELETE', `/whatsapp/templates/${t.id}`).then(() => true), 'Template deleted').then((ok) => ok && reload())}>
                Delete
              </ConfirmButton>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The file a template's image, video or document header sends, unless the agent picks another. */
function TemplateFile({ t, onDone }: { t: WaTemplate; onDone: () => void }) {
  const set = (f: FileView | null) => void act(() => api('PUT', `/whatsapp/templates/${t.id}/header-file`, { fileId: f?.id ?? null }).then(() => true), f ? 'Header file saved' : 'Header file removed').then((ok) => ok && onDone());
  return (
    <div className="mt-2">
      <HeaderFilePicker format={t.headerFormat!} file={t.headerFile} onFile={set} label={`${MEDIA_HEADERS[t.headerFormat!].label} sent with ${t.name}`} />
    </div>
  );
}

type HeaderKind = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';

function NewTemplate({ accountId, onDone }: { accountId: string; onDone: () => void }) {
  const [f, setF] = useState({ name: '', language: 'en', category: 'UTILITY' as 'UTILITY' | 'MARKETING', header: '', body: '', footer: '' });
  const [kind, setKind] = useState<HeaderKind>('NONE');
  const [example, setExample] = useState<FileView | null>(null);
  const media = kind !== 'NONE' && kind !== 'TEXT';
  const [examples, setExamples] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const draft: WaTemplate = {
    id: '',
    accountId,
    name: f.name,
    language: f.language,
    category: f.category,
    status: '',
    rejectedReason: null,
    parameterFormat: 'POSITIONAL',
    sendable: false,
    unsupported: null,
    headerFormat: media ? kind : kind === 'TEXT' && f.header ? 'TEXT' : null,
    headerFile: example,
    components: [
      ...(media ? [{ type: 'HEADER', format: kind }] : kind === 'TEXT' && f.header ? [{ type: 'HEADER', format: 'TEXT', text: f.header }] : []),
      { type: 'BODY', text: f.body },
      ...(f.footer ? [{ type: 'FOOTER', text: f.footer }] : []),
    ],
  };
  const blanks = blanksOf(draft);
  async function save() {
    setBusy(true);
    const ok = await act(
      () =>
        api('POST', '/whatsapp/templates', {
          accountId,
          ...f,
          headerFormat: media ? kind : 'TEXT',
          ...(media && example ? { headerFileId: example.id } : {}),
          header: kind === 'TEXT' && f.header ? f.header : undefined,
          footer: f.footer || undefined,
          examples: blanks.map((_, i) => examples[i] ?? ''),
        }),
      'Sent to Meta for approval',
    );
    setBusy(false);
    if (ok) onDone();
  }
  return (
    <div className="mb-4 rounded-lg border border-ion/40 p-3" aria-label="New template">
      <Field label="Template name" hint="Lower case letters, digits and _, like order_ready">
        {(id) => <input id={id} className={`${inputClass} tabular`} value={f.name} onChange={(e) => set('name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} />}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Language" hint="en, en_US, sw…">
          {(id) => <input id={id} className={`${inputClass} tabular`} value={f.language} onChange={(e) => set('language', e.target.value.trim())} />}
        </Field>
        <Field label="Category" hint="Marketing costs more and needs the customer's opt-in">
          {(id) => (
            <select id={id} className={inputClass} value={f.category} onChange={(e) => set('category', e.target.value as typeof f.category)}>
              <option value="UTILITY">Utility (orders, accounts, appointments)</option>
              <option value="MARKETING">Marketing (offers, news)</option>
            </select>
          )}
        </Field>
      </div>
      <Field label="Header">
        {(id) => (
          <select id={id} className={inputClass} value={kind} onChange={(e) => (setKind(e.target.value as HeaderKind), setExample(null))}>
            <option value="NONE">None</option>
            <option value="TEXT">Text</option>
            <option value="IMAGE">Image</option>
            <option value="VIDEO">Video</option>
            <option value="DOCUMENT">Document (PDF)</option>
          </select>
        )}
      </Field>
      {kind === 'TEXT' ? <Field label="Header text">{(id) => <input id={id} className={inputClass} value={f.header} maxLength={60} onChange={(e) => set('header', e.target.value)} />}</Field> : null}
      {media ? (
        <>
          <HeaderFilePicker format={kind} file={example} onFile={setExample} label="Example file" />
          <p className="-mt-3 mb-4 text-[11px] text-ink-3">Meta reviews the template with it. Nova then sends it, unless the agent picks another file.</p>
        </>
      ) : null}
      <Field label="Message" hint="Blanks are {{1}}, {{2}}… filled in by the agent when sending. It cannot start or end with a blank.">
        {(id) => <textarea id={id} rows={4} className={inputClass} value={f.body} maxLength={1024} onChange={(e) => set('body', e.target.value)} placeholder="Hi {{1}}, your order {{2}} is ready for pickup." />}
      </Field>
      <Field label="Footer (optional)">{(id) => <input id={id} className={inputClass} value={f.footer} maxLength={60} onChange={(e) => set('footer', e.target.value)} />}</Field>
      {blanks.map((b, i) => (
        <Field key={b} label={`Example for {{${b}}}`} hint={i === 0 ? 'Meta reviews the template with these examples' : undefined}>
          {(id) => <input id={id} className={inputClass} value={examples[i] ?? ''} onChange={(e) => setExamples((x) => Object.assign([...x], { [i]: e.target.value }))} />}
        </Field>
      ))}
      <div className="flex gap-2">
        <Button tone="primary" className="px-3 py-1.5 text-sm" disabled={busy || !f.name || !f.body.trim() || !f.language || (media && !example)} onClick={() => void save()}>
          {busy ? 'Sending…' : 'Send to Meta for approval'}
        </Button>
        <Button tone="ghost" className="px-3 py-1.5 text-sm" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
